import React, { useEffect, useRef, useState } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

const pageFiles = {
  index: '/design-system/index.html',
  about: '/design-system/about.html',
  contact: '/design-system/contact.html'
};

const pageTitles = {
  index: 'Fishman Health Insurance Brokers',
  about: 'Meet Our Team | Fishman Health',
  contact: 'Get Started | Fishman Health'
};

const functionalStyles = `
  :host{line-height:normal}
  .deeper{overflow:hidden}
  .submission-status{margin-top:16px;padding:14px 16px;border:1px solid rgba(214,235,248,.2);border-radius:4px;color:#d6ebf8;font:600 13px/1.55 Inter,sans-serif}
  .submission-status.success{background:rgba(61,138,114,.18);border-color:rgba(100,190,155,.35)}
  .submission-status.error{background:rgba(182,97,63,.18);border-color:rgba(220,135,105,.38)}
  .submission-status[hidden]{display:none}
  form button[disabled]{opacity:.58;cursor:wait}
`;

const preparePage = (source) => {
  const cssBlocks = [...source.matchAll(/<style>([\s\S]*?)<\/style>/gi)]
    .map((match) => match[1]);
  const bodyMatch = source.match(/<body[^>]*>([\s\S]*?)<script>/i);
  if (!cssBlocks.length || !bodyMatch) throw new Error('The supplied design page is incomplete.');

  // The supplied pages were authored as complete documents. Move their two
  // document-level selectors onto the shadow host so the original rules can
  // run unchanged without either the old app CSS or admin CSS leaking in.
  const isolatedCss = cssBlocks.join('\n')
    .replace(':root{', ':host{')
    .replace('html{scroll-behavior:smooth}', ':host{scroll-behavior:smooth}')
    .replace('body{', ':host{');

  const markup = bodyMatch[1]
    .replace(/src="data:image\/png;base64,[^"]+"/g, 'src="/fish_logo_transparent.png"')
    .replaceAll('https://fishmanhealth.com/ben_face.JPG', '/ben_face.JPG')
    .replaceAll('https://fishmanhealth.com/stef_face.jpg', '/stef_face.jpg')
    .replaceAll('https://fishmanhealth.com/louis_face.jpeg', '/louis_face.jpeg')
    .replaceAll('href="index.html"', 'href="/"')
    .replaceAll('href="about.html"', 'href="/about"')
    .replaceAll('href="contact.html"', 'href="/contact"')
    .replace(
      '<form action="mailto:ben@fishmanhealth.com" method="post" enctype="text/plain">',
      '<form data-referral-form novalidate>'
    );

  return { css: `${isolatedCss}\n${functionalStyles}`, markup };
};

const DesignPage = ({ page }) => {
  const rootRef = useRef(null);
  const [design, setDesign] = useState({ css: '', markup: '' });
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoadError('');
    setDesign({ css: '', markup: '' });

    fetch(pageFiles[page], { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load ${pageFiles[page]}`);
        return response.text();
      })
      .then((source) => setDesign(preparePage(source)))
      .catch((error) => {
        if (error.name !== 'AbortError') setLoadError(error.message);
      });

    return () => controller.abort();
  }, [page]);

  useEffect(() => {
    const host = rootRef.current;
    if (!host || !design.markup) return undefined;

    const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = design.css;
    const pageTemplate = document.createElement('template');
    pageTemplate.innerHTML = design.markup;
    root.replaceChildren(style, pageTemplate.content.cloneNode(true));

    document.body.dataset.page = page;
    document.title = pageTitles[page];
    document.documentElement.style.scrollBehavior = 'smooth';

    root.querySelectorAll('.snow').forEach((container) => {
      const count = Number(container.dataset.snow) || 12;
      for (let index = 0; index < count; index += 1) {
        const flake = document.createElement('span');
        const size = 1.5 + Math.random() * 2.5;
        flake.className = 'flake';
        flake.style.width = `${size}px`;
        flake.style.height = `${size}px`;
        flake.style.left = `${Math.random() * 100}%`;
        flake.style.setProperty('--sx', `${Math.random() * 50 - 25}px`);
        flake.style.animationDuration = `${16 + Math.random() * 20}s`;
        flake.style.animationDelay = `${-Math.random() * 30}s`;
        container.appendChild(flake);
      }
    });

    const parallaxLayers = [
      [root.querySelector('.reef-far'), 0.12],
      [root.querySelector('.reef-mid'), 0.06],
      [root.querySelector('.fish-wrap'), 0.08]
    ].filter(([element]) => element);
    let animationFrame = null;
    const handleScroll = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        parallaxLayers.forEach(([element, speed]) => {
          element.style.translate = `0 ${window.scrollY * speed}px`;
        });
        animationFrame = null;
      });
    };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) window.addEventListener('scroll', handleScroll, { passive: true });

    const observer = 'IntersectionObserver' in window
      ? new window.IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      }), { threshold: 0.15 })
      : null;
    root.querySelectorAll('.rv').forEach((element) => {
      if (observer) observer.observe(element);
      else element.classList.add('in');
    });

    root.querySelectorAll('.nav-links a[data-page]').forEach((anchor) => {
      anchor.classList.toggle('active', anchor.dataset.page === page);
    });

    const bubbleWrap = root.querySelector('#bubbles');
    if (bubbleWrap) {
      for (let index = 0; index < 24; index += 1) {
        const bubble = document.createElement('span');
        const size = 4 + Math.random() * 14;
        bubble.className = 'bubble';
        bubble.style.width = `${size}px`;
        bubble.style.height = `${size}px`;
        bubble.style.left = `${Math.random() * 100}%`;
        bubble.style.setProperty('--dx', `${Math.random() * 70 - 35}px`);
        bubble.style.animationDuration = `${10 + Math.random() * 13}s`;
        bubble.style.animationDelay = `${-Math.random() * 18}s`;
        bubbleWrap.appendChild(bubble);
      }
    }

    const form = root.querySelector('[data-referral-form]');
    const handleSubmit = async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const button = form.querySelector('button[type="submit"]');
      let status = root.querySelector('.submission-status');
      if (!status) {
        status = document.createElement('div');
        status.className = 'submission-status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        form.insertAdjacentElement('afterend', status);
      }

      const fields = new FormData(form);
      const payload = {
        name: String(fields.get('full_name') || '').trim(),
        email: String(fields.get('email') || '').trim(),
        phone: String(fields.get('phone') || '').trim()
      };

      button.disabled = true;
      button.textContent = 'Sending…';
      status.hidden = true;

      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.referral), {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) throw new Error(result.error || 'Submission failed');
        status.className = 'submission-status success';
        status.textContent = 'Thank you. Your information was sent and the Fishman team has been notified.';
        status.hidden = false;
        form.reset();
      } catch (error) {
        status.className = 'submission-status error';
        status.textContent = 'We could not send your information. Please try again or email us directly.';
        status.hidden = false;
      } finally {
        button.disabled = false;
        button.textContent = 'Submit My Information';
      }
    };
    if (form) form.addEventListener('submit', handleSubmit);

    return () => {
      if (!reduceMotion) window.removeEventListener('scroll', handleScroll);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      if (observer) observer.disconnect();
      if (form) form.removeEventListener('submit', handleSubmit);
      delete document.body.dataset.page;
      document.documentElement.style.removeProperty('scroll-behavior');
      root.replaceChildren();
    };
  }, [design.css, design.markup, page]);

  return (
    <div
      ref={rootRef}
      className="design-page-shell"
      aria-busy={!design.markup && !loadError}
      aria-label={loadError || undefined}
    />
  );
};

export default DesignPage;
